/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HttpErrorResponse } from '@angular/common/http';
import type { AddedDevices } from 'src/app/shared/interfaces/financials/added-devices-response.interface';
import type { PermissionTypes } from 'src/app/shared/interfaces/permissions.interface';

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  FeeIdListItem,
  IAccount,
  IApplication,
  IState,
} from 'src/app/shared/interfaces/account/account.interface';
import { IAddress, IContact } from 'src/app/shared/interfaces/common.interface';
import { GQLResponse } from 'src/app/shared/interfaces/graphql.interface';
import { IGroupEnrollment } from 'src/app/shared/interfaces/group-enrollment/group-enrollment.interface';
import {
  IProviderGroup,
  type IProviderGroupCreateREST,
  type IProviderGroupUpdateREST,
  IStatusDatesValue,
} from 'src/app/shared/interfaces/provider/provider-group.interfaces';
import {
  type IBatchRemittance,
  IProvider,
  IProviderAPI,
  IProviderStatusDates,
  providerBatchRemittanceExtractor,
  ProviderFilters,
  providerStatusDatesExtractor,
} from 'src/app/shared/interfaces/provider/provider.interfaces';
import { AlertService } from 'src/app/shared/services/alert.service';
import { CommonGraphqlService } from 'src/app/shared/services/common.graphql.service';
import { DatepickerService } from 'src/app/shared/services/datepicker.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ProviderGroupGQLService } from 'src/app/shared/services/provider/provider-group.graphql.service';
import { ProviderGroupService } from 'src/app/shared/services/provider/provider-group.service';
import { ProviderGQLService } from 'src/app/shared/services/provider/provider.graphql.service';
import { ProviderService } from 'src/app/shared/services/provider/provider.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';
import {
  DEFAULT_MODAL_OPTIONS,
  EMAIL_REGEX,
} from 'src/app/shared/variables/variables';

import { FinancialsComponent } from '../../financials/financials.component';
import { AddGroupEnrollmentsModalComponent } from '../../group/add-group-enrollments-modal/add-group-enrollments-modal.component';
import { QualifiersGridComponent } from '../../qualifiers-grid/qualifiers-grid.component';
import { AddProviderSetupModalComponent } from '../add-provider-setup-modal/add-provider-setup-modal.component';

@Component({
  selector: 'app-create-provider-group-modal',
  templateUrl: './create-provider-group-modal.component.html',
})
export class CreateProviderGroupModalComponent implements OnInit, OnDestroy {
  @ViewChild(QualifiersGridComponent) qualifiersGrid: QualifiersGridComponent;
  @ViewChild(FinancialsComponent) financials: FinancialsComponent;

  @Input() account: IAccount;
  @Input() groupId: string;
  @Input() isReadOnly = false;
  @Input() statusDates: IProviderStatusDates;

  providerGroupForm: UntypedFormGroup;
  providerGroupProcessorForm: UntypedFormGroup;
  providerGroupFinancialsForm: UntypedFormGroup;
  permissionTypes: PermissionTypes;

  providerGroup?: IProviderGroup;
  applications: IApplication[];
  states: IState[];
  fees: FeeIdListItem[];
  application: IApplication;

  userFullName: string;
  activeTabNumber = 1;

  isEditMode: boolean;
  isViewMode: boolean;
  isDev: boolean;
  isClientAdmin?: boolean;
  isLoading = true;

  existingGroupId: number;

  statusDateValue: IStatusDatesValue;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private providerGroupGQLService: ProviderGroupGQLService,
    private providerGroupService: ProviderGroupService,
    private srvGqlProviders: ProviderGQLService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private modal: NgbModal,
    private commonGraphqlService: CommonGraphqlService,
    private activeModal: NgbActiveModal,
    private userStateService: UserStateService,
    private datepickerService: DatepickerService,
    private modalService: ModalService,
    private providerService: ProviderService,
  ) {
    this.userFullName = this.userStateService.userFullName;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isEditMode = !!this.groupId;
    this.isViewMode = true;
    this.isClientAdmin = false;
    const currentUser = this.userStateService.user;
    const permissions = currentUser?.permissions;

    const containsAccounts = permissions?.some((permission) => {
      return (
        permission.name === 'Accounts' ||
        permission.name === 'Client Admin - Accounts'
      );
    });
    this.isClientAdmin = permissions?.some((permission) => {
      return permission.name === 'Client Admin - Accounts';
    });
    this.isViewMode = !containsAccounts && !this.isClientAdmin;
    if (!this.isReadOnly) {
      this.isReadOnly = this.isViewMode;
    }

    this.setProviderGroupForm();
    this.setProviderGroupProcessorForm();
    this.setProviderGroupFinancialsForm();

    const requests = [this.commonGraphqlService.getApplications()];

    const accountEntities = combineLatest(requests);

    this.subscriptions.add(
      accountEntities.subscribe(() => {
        this.isLoading = false;

        if (!this.isEditMode) {
          this.patchFormByAccount();
        }
        this.loadModalInfo();
      }),
    );
  }

  close() {
    this.activeModal.dismiss();
  }

  openProviderSetUp() {
    const modal = this.modal.open(AddProviderSetupModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'md',
      scrollable: false,
    });

    const mi = modal.componentInstance as AddProviderSetupModalComponent;
    const pg = this.providerGroupForm.getRawValue();

    mi.predefinedData = {
      accountNumber: pg.accountNumber,
      groupId: pg.groupId,
      feeSchedule: pg.feeSchedule,
      certified: pg.certified,
      contact: { ...pg.contact },
      address: { ...pg.address },
    } as Partial<IProviderAPI>;

    mi.providerStatusDates = providerStatusDatesExtractor(
      pg as IProviderStatusDates,
    );
    mi.batchRemittance = providerBatchRemittanceExtractor(
      pg as IBatchRemittance,
    );

    mi.groupId = this.providerGroup?.groupId
      ? this.providerGroup.groupId
      : undefined;

    modal.result.then(
      () => {
        this.doProviderGridReload();
        this.save();
        this.showSaveMessage();
      },
      (error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      },
    );
  }

  doProviderGridReload() {
    this.isLoading = true;

    const filter: ProviderFilters = { groupId: this.groupId };

    this.subscriptions.add(
      this.srvGqlProviders
        .getProviders({
          filter,
          orderby: 'id',
          direction: 'ASC',
        })
        .subscribe({
          next: (p) => {
            this.isLoading = false;
            if (this.providerGroup) {
              this.providerGroup.providers = p.nodes;
            }
          },
          error: (error: unknown) => {
            this.isLoading = false;

            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          },
        }),
    );
  }

  //review
  doProviderGridScroll() {
    ///wtf?
  }

  save() {
    this.isLoading = true;

    const providerGroupFormValue = this.providerGroupForm.getRawValue();
    const processorGroupFormValue =
      this.providerGroupProcessorForm.getRawValue();
    const providerGroupFinancialsFormValue =
      this.providerGroupFinancialsForm.getRawValue();

    const formValue = {
      ...providerGroupFormValue,
      ...this.statusDateValue,
    };

    const processor = {
      ...processorGroupFormValue,
      pmIdentifier: processorGroupFormValue.pMIdentifier,
    };

    const financials = {
      ...providerGroupFinancialsFormValue,
      accountCredentialKey:
        providerGroupFinancialsFormValue.accountCredentialKey,
      allowACH: providerGroupFinancialsFormValue.allowACH ?? 'N',
      isDev: providerGroupFinancialsFormValue.isDev ?? true,
      achCredentialKey: providerGroupFinancialsFormValue.achCredentialKey,
    };

    const currentUser = this.userStateService.user;

    const permissions = currentUser?.permissions;

    const containsFinancials = permissions?.some((permission) => {
      return permission.name === 'Financial Module';
    });

    const qualifiers = this.qualifiersGrid.getQualifiersData();

    let devices: AddedDevices[] = [];
    if (containsFinancials) {
      devices = this.financials.getDevicesData();
    }

    const providerGroup = {
      ...formValue,
      ...processor,
      ...financials,
      qualifiers,
      devices,
      groupId: providerGroupFormValue.groupId
        ? providerGroupFormValue.groupId
        : this.groupId,
      glueEM: providerGroupFormValue.glueEM
        ? providerGroupFormValue.glueEM
        : 'N',
      dateLastCertified: providerGroupFormValue.dateLastCertified
        ? providerGroupFormValue.dateLastCertified
        : null,
      ecsStatus: formValue.status.ecsStatus,
      eraStatus: formValue.status.eraStatus,
      claimStatusStatus: formValue.status.claimStatusStatus,
      eligibilityStatus: formValue.status.eligibilityStatus,
      statementStatus: formValue.status.statementStatus,
    };

    if (providerGroupFormValue.genericProvider === 'Y') {
      const request$ = this.isEditMode
        ? this.providerGroupService.editProviderGroup(
            providerGroup as IProviderGroupUpdateREST,
          )
        : this.providerGroupService.createProviderGroup(
            providerGroup as IProviderGroupCreateREST,
          );

      this.subscriptions.add(
        request$.subscribe({
          next: (updatedProviderGroup: any) => {
            this.providerGroup = updatedProviderGroup;
            if (this.providerGroup?.groupId) {
              this.groupId = this.providerGroup.groupId;
            }

            this.isLoading = false;
            if (this.isEditMode) {
              this.showSaveMessage();
            } else {
              const provider: IProviderAPI = {
                id: '',
                pms: updatedProviderGroup.pms,
                accountNumber: updatedProviderGroup.accountNumber,
                providerGroupId: updatedProviderGroup.groupId,
                lastName: updatedProviderGroup.groupId,
                firstName: 'ALL PROVIDERS',
                address: {
                  line1: updatedProviderGroup.address.line1,
                  line2: updatedProviderGroup.address.line2,
                  city: updatedProviderGroup.address.city,
                  state: updatedProviderGroup.address.state,
                  zipcode: updatedProviderGroup.address.zipcode,
                } as IAddress,
                contact: {
                  name: updatedProviderGroup.contact.name,
                  phone: updatedProviderGroup.contact.phone,
                  fax: updatedProviderGroup.contact.fax,
                  email: updatedProviderGroup.contact.email,
                  title: updatedProviderGroup.contact.title,
                } as IContact,
                notes: updatedProviderGroup.notes,
                ecsStatus: updatedProviderGroup.ecsStatus,
                eraStatus: updatedProviderGroup.eraStatus,
                claimStatusStatus: updatedProviderGroup.claimStatusStatus,
                eligibilityStatus: updatedProviderGroup.eligibilityStatus,
                statementStatus: updatedProviderGroup.statementStatus,
                feeSchedule: updatedProviderGroup.feeSchedule,
                certified: updatedProviderGroup.certified,
                groupId: updatedProviderGroup.groupId,
                name: updatedProviderGroup.name,
              };

              this.providerService.createProvider(provider).subscribe({
                next: () => {
                  this.isLoading = false;
                  this.alertService.success(
                    'generic provider has been added successfully',
                  );
                  this.modalService
                    .open('confirm', {
                      bodyTitle:
                        'We have added generic provider for you. Do you want to add another provider to provider group?',
                      confirmButtonText: 'Yes',
                      cancelButtonText: 'No',
                    })
                    .result.then(() => {
                      this.isEditMode = true;
                      this.openProviderSetUp();
                    })
                    .catch(() => {
                      this.showSaveMessage();
                    });
                },
                error: (error: unknown) => {
                  this.isLoading = false;
                  if (error) {
                    this.alertService.error(
                      error as HttpErrorResponse | string,
                    );
                  }
                },
              });
            }
          },
          error: (error: unknown) => {
            this.isLoading = false;
            this.alertService.error(error as HttpErrorResponse | string);
          },
        }),
      );
    } else {
      const request$ = this.isEditMode
        ? this.providerGroupService.editProviderGroup(
            providerGroup as IProviderGroupUpdateREST,
          )
        : this.providerGroupService.createProviderGroup(
            providerGroup as IProviderGroupCreateREST,
          );

      this.subscriptions.add(
        request$.subscribe({
          next: (updatedProviderGroup) => {
            this.providerGroup = updatedProviderGroup as IProviderGroup;
            if (this.providerGroup.groupId) {
              this.groupId = this.providerGroup.groupId;
            }

            this.isLoading = false;
            if (this.isEditMode) {
              this.showSaveMessage();
            } else {
              this.modalService
                .open('confirm', {
                  bodyTitle: 'Do you want to add provider to provider group?',
                  confirmButtonText: 'Yes',
                  cancelButtonText: 'No',
                })
                .result.then(() => {
                  this.isEditMode = true;
                  this.openProviderSetUp();
                })
                .catch(() => {
                  this.showSaveMessage();
                });
            }
          },
          error: (error: unknown) => {
            this.isLoading = false;

            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          },
        }),
      );
    }
  }

  getStatusDate(status: boolean): string {
    return status ? this.datepickerService.formatDate(new Date()) : '';
  }

  onCheckboxStatusChanged($event: any, fieldName: string) {
    const date = this.datepickerService.formatDate(new Date());
    const input = this.providerGroupForm.get(fieldName);

    if (input) {
      if ($event.target.checked) {
        input.setValue(date);
      } else {
        input.setValue('');
      }
    }
  }

  addGroupEnrollment() {
    if (!this.providerGroup?.groupId) {
      return;
    }

    const groupEnrollment = {
      id: this.providerGroup.groupId,
      providerGroup: this.providerGroup,
      processorId: this.providerGroup.processorId,
    } as IGroupEnrollment;

    const modal = this.modal.open(AddGroupEnrollmentsModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'md',
    });
    const modalInstance =
      modal.componentInstance as AddGroupEnrollmentsModalComponent;

    modalInstance.groupEnrollment = groupEnrollment;

    modal.result
      .then(() => {
        this.alertService.info('groupEnrollment created');
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  get providers(): IProvider[] {
    return this.providerGroup?.providers ?? [];
  }

  //review
  private setProviderGroupForm() {
    this.providerGroupForm = this.fb.group({
      accountNumber: this.fb.control(
        { value: '', disabled: true },
        Validators.required,
      ),
      pms: this.fb.control(
        { value: '', disabled: this.isClientAdmin && !this.isEditMode },
        Validators.required,
      ),
      reportNumber: this.fb.control(0),
      dateLastCertified: this.fb.control(''),
      groupId: this.fb.control(''),
      lastUpdatedBy: this.fb.control(''),
      feeSchedule: this.fb.control(
        { value: '', disabled: this.isClientAdmin && this.isEditMode },
        Validators.required,
      ),
      name: this.fb.control(
        { value: '', disabled: false },
        Validators.required,
      ),
      rcm: this.fb.control(''),
      address: this.fb.group({
        line1: [''],
        line2: [''],
        zipcode: [''],
        state: [''],
        city: [''],
      }),
      alphaII: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      appSenderCode: this.fb.control(''),
      backLink: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      batch277ca: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      batch277u: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      batchCSV: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      batchHumanReadable: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      batchMachineReadable: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      batchXml: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      certified: this.fb.control(
        { value: '', disabled: this.isClientAdmin && !this.isEditMode },
        Validators.required,
      ),
      combineERA: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      contact: this.fb.group({
        name: this.fb.control(''),
        phone: this.fb.control('', [Validators.maxLength(10)]),
        fax: this.fb.control(''),
        email: this.fb.control('', Validators.pattern(EMAIL_REGEX)),
        title: this.fb.control(''),
      }),

      status: this.fb.group({
        eraStatus: this.fb.control(''),
        ecsStatus: this.fb.control(''),
        claimStatusStatus: this.fb.control(''),
        eligibilityStatus: this.fb.control(''),
        statementStatus: this.fb.control(''),

        eraDate: this.fb.control(''),
        ecsDate: this.fb.control(''),
        claimStatusDate: this.fb.control(''),
        eligibilityDate: this.fb.control(''),
        statementDate: this.fb.control(''),

        dateSetup: this.fb.control(''),
        dateTerminated: this.fb.control(''),
        dateLastDbUpdate: this.fb.control(''),
        dateLastDbUpdated: this.fb.control(''),
        lastUpdatedBy: this.fb.control(''),
        lastDbUpdatedBy: this.fb.control(''),
      }),

      ftpKey: this.fb.control(''),
      glueEM: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      humanReadable271: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      instAppSenderCode: this.fb.control(''),
      instNM109IdCode: this.fb.control(''),
      instProcessorId: this.fb.control(''),
      instSubmitterId: this.fb.control(''),
      lockBox: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      lockboxProcessorId: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      mailboxId: this.fb.control(''),
      newStatements: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      nM109IdCode: this.fb.control(''),
      notes: this.fb.control(''),
      pmIdentifier: this.fb.control(''),
      rejectPrintClaims: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remit277ca: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remit277u: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remitCsv: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remitCSVDaily: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remitEraSummary: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remitHumanReadable: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remitMachineReadable: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      remitXml: this.fb.control({
        value: 'N',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      submitterId: this.fb.control(''),
      universalReport: this.fb.control(''),
      inputGenerates999: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      genericProvider: this.fb.control({
        value: '',
        disabled: this.isClientAdmin && this.isEditMode,
      }),
      accountCredentialKey: this.fb.control(''),
    });
  }

  private setProviderGroupProcessorForm() {
    this.providerGroupProcessorForm = this.fb.group({
      pMIdentifier: [''],
      processorId: [''],
      instProcessorId: [''],
      submitterId: [''],
      instSubmitterId: [''],
      appSenderCode: [''],
      instAppSenderCode: [''],
      nM109IdCode: [''],
      instNM109IdCode: [''],
      partialRedirectProfessional: ['N'],
      partialRedirectInstitutional: ['N'],
    });
  }

  private setProviderGroupFinancialsForm() {
    this.providerGroupFinancialsForm = this.fb.group({
      accountCredentialKey: [''],
      allowACH: ['N'],
      isDev: Boolean,
      achCredentialKey: [''],
    });
  }

  private loadModalInfo() {
    const providerGroupRequest$ = this.providerGroupGQLService.getProviderGroup(
      this.groupId,
    );

    const inputsRequests = (
      applicationCode: string,
      providerGroup?: IProviderGroup,
    ) => {
      return [
        this.commonGraphqlService.getApplications(),
        this.commonGraphqlService.getStates(),
        this.providerGroupGQLService.getFeesIdList(),
        this.commonGraphqlService.getApplication(applicationCode),
        of(providerGroup),
      ];
    };

    const editRequests$ = providerGroupRequest$.pipe(
      switchMap((providerGroup) => {
        return combineLatest(
          inputsRequests(providerGroup.pMS ?? '', providerGroup),
        );
      }),
    );

    const createRequestes$ = combineLatest(inputsRequests(this.account.pMS));

    const requests$ = this.isEditMode ? editRequests$ : createRequestes$;

    this.subscriptions.add(
      requests$.subscribe({
        next: ([applications, states, fees, application, providerGroup]) => {
          this.applications = applications as IApplication[];
          this.states = states as IState[];
          this.fees = (fees as GQLResponse<FeeIdListItem>).nodes;

          if (this.applications.length === 0) {
            const apps = new Array<IApplication>();
            apps.push(application as IApplication);
            this.applications = apps;
          }

          this.application = application as IApplication;
          this.providerGroup = providerGroup
            ? (providerGroup as IProviderGroup)
            : undefined;

          this.patchFormByApplication();
          if (this.providerGroup) {
            this.patchFormByProviderGroup();
          }

          if (!this.isEditMode) {
            this.providerGroupForm.patchValue(this.statusDates);
          }

          if (
            this.isEditMode &&
            this.isClientAdmin &&
            this.providerGroup?.pMIdentifier &&
            this.providerGroup.pMIdentifier !== ''
          ) {
            this.providerGroupProcessorForm.disable();
          }

          this.isLoading = false;

          const allowACH = this.providerGroupFinancialsForm.get('allowACH');
          if (allowACH && allowACH.value !== 'Y') {
            const allowACHKeyInput: HTMLInputElement | null =
              document.querySelector('#ach-key-input');

            if (allowACHKeyInput) {
              allowACHKeyInput.disabled = true;
            }
          }
        },
        error: (error: unknown) => {
          this.isLoading = false;
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
        },
      }),
    );
  }

  private patchFormByApplication() {
    const { application } = this;

    this.providerGroupForm.patchValue({
      ...application,
      //Batch reports
      //batchCSV: application.batchCsv,
      //batchXml: application.batchxml,

      // remittance
      //remitCSVDaily: application.remitCsvDaily,
    });
  }

  //Patch with account occurs when provider group creates
  private patchFormByAccount() {
    if (!this.account.address) {
      return;
    }
    this.providerGroupForm.patchValue({
      address: {
        line1: this.account.address.line1,
        line2: this.account.address.line2,
        city: this.account.address.city,
        state: this.account.address.state,
        zipcode: this.account.address.zipcode,
      },
      pms: this.account.pMS,
      status: {
        ecsStatus: this.account.eCSStatus,
        eraStatus: this.account.eRAStatus,
        ecsDate: this.account.eCSDate,
        eraDate: this.account.eRADate,
        ...this.account,
      },
      batch277ca: 'Y',
      remit277ca: 'Y',
      certified: 'P',
      ...this.account,
    });
  }

  // rewew
  private patchFormByProviderGroup() {
    if (!this.providerGroup) {
      return;
    }

    this.providerGroupForm.patchValue({
      ...this.providerGroup,
      address: {
        line1: this.providerGroup.addressLine1,
        line2: this.providerGroup.addressLine2,
        city: this.providerGroup.addressCity,
        state: this.providerGroup.addressState,
        zipcode: this.providerGroup.addressZipcode,
      },
      contact: {
        title: this.providerGroup.contactTitle,
        name: this.providerGroup.contactName,
        phone: this.providerGroup.contactPhone,
        fax: this.providerGroup.contactFax,
        email: this.providerGroup.contactEmail,
      },
      pms: this.providerGroup.pMS,

      rcm: this.providerGroup.rCM,

      status: {
        ...this.providerGroup,
        ecsStatus: this.providerGroup.eCSStatus,
        eraStatus: this.providerGroup.eRAStatus,
        ecsDate: this.providerGroup.eCSDate,
        eraDate: this.providerGroup.eRADate,
      },
    });

    this.providerGroupProcessorForm.patchValue({
      ...this.providerGroup,
    });

    this.providerGroupFinancialsForm.patchValue({
      ...this.providerGroup,
    });

    this.providerGroupForm.markAllAsTouched();
  }

  private showSaveMessage() {
    this.alertService.success(
      this.isEditMode ? 'Edit provider group' : 'Add new provider group',
      'Provider group has been sucessfully ' +
        (this.isEditMode ? 'modified' : 'added'),
    );
    this.activeModal.close(this.providerGroup);
  }
}
